import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ImpersonationContext } from '@center-technologies/shared-api-clients';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { hasAdapter } from './adapterRegistry';
import { CATALOG } from '@center-platform/component-catalog';
import type { CompositionResolver } from '@center-platform/space-format/expand';
import type { SpaceDocument } from 'types/space';
import type { CompositionDefinition, CompositionRef } from 'types/composition';

const SPACE_CATALOG_TYPES = new Set(
  CATALOG.filter((entry) => entry.surfaces.includes('space')).map((entry) => entry.type)
);

/**
 * Published versions are immutable — safe to cache for the whole session, across every Space
 * instance. Keyed by `system_name@version`, which is unique only WITHIN a tenant: compositions are
 * unique on `(tenant_id, system_name)`.
 *
 * The PWA DOES switch tenants in-session: impersonation changes the effective user AND org with no
 * reload (`useAppInitialization.ts` — "impersonation switches the effective user/org"), and
 * `ApiClientsProvider` deliberately does not rebuild its clients across that switch. So a
 * `view`-gated definition cached for identity A in org X would otherwise be served to identity B in
 * org Y whenever the two orgs share a `system_name` — and sharing one is the normal case, since
 * `hero_section` is exactly the kind of name two tenants both use. The cache is therefore scoped to
 * the acting org and dropped wholesale when that org changes, so the previous tenant's entries are
 * unreachable by construction rather than by a reset hook happening to be mounted. Same
 * identity-shaped invalidation as `useSchemaTypeCacheReset`, kept local because nothing outside
 * this module owns these entries.
 */
let publishedVersionCache = new Map<string, CompositionDefinition>();
let publishedCacheOrgId: string | undefined | null = null;

/**
 * The tenant bucket is REPLACED, never `.clear()`ed. An in-flight effect run captures the Map
 * object before its `await`, so clearing in place would hand the previous tenant's settling request
 * a live handle on the NEW tenant's bucket: org A's request resolves after the switch and writes
 * org A's `view`-gated definition into org B's cache, which is the very leak the org scoping exists
 * to prevent. Replacing orphans the old instance — a stale capture then writes to a Map nothing
 * will ever read. (`unpinnedCacheRef` is already safe for exactly this reason: it is assigned a
 * `new Map()` rather than cleared.)
 */
function publishedCacheForOrg(orgId: string | undefined): Map<string, CompositionDefinition> {
  if (publishedCacheOrgId !== orgId) {
    publishedVersionCache = new Map();
    publishedCacheOrgId = orgId;
  }
  return publishedVersionCache;
}

/**
 * Test-only. The bucket is module state, so without this a suite's entries survive into the next
 * test and coverage silently depends on every test picking a distinct `system_name`.
 */
export function __resetPublishedCompositionCache(): void {
  publishedVersionCache = new Map();
  publishedCacheOrgId = null;
}

interface CompositionResponse {
  version: number | null;
  is_published: boolean;
  definition: CompositionDefinition | null;
}

function refCacheKey(ref: CompositionRef): string {
  return `${ref.systemName}@${ref.version ?? 'latest'}`;
}

/**
 * A Space document references a composition the same way A2UI addresses any component: by
 * `component: '<system_name>'`, with no adapter and no catalog entry registered for that type.
 * Anything else IS a real, code-backed component and must not be fetched.
 *
 * Only sees TOP-LEVEL refs — a ref nested inside a fetched composition's own subtree is invisible
 * here, since discovering it would require fetching that composition first. Used only as the
 * pre-lockfile fallback (see `findLockfileRefs`); it is the reason today's ref-by-ref resolution
 * cannot resolve a composition-inside-a-composition (a real gap the lockfile closes).
 */
function findCompositionRefs(document: SpaceDocument): CompositionRef[] {
  const refs = new Map<string, CompositionRef>();
  document.a2ui.components.forEach((entry) => {
    const type = entry.component;
    if (hasAdapter(type) || SPACE_CATALOG_TYPES.has(type)) return;
    const ref: CompositionRef = { systemName: type, version: entry.componentVersion as number | undefined };
    refs.set(refCacheKey(ref), ref);
  });
  return Array.from(refs.values());
}

/**
 * Every composition ref reachable from the artifact, transitively, at the exact version it
 * resolved to when the lockfile was computed (`computeLockfile`, write-time only) — including refs
 * nested inside a referenced composition's own subtree, which `findCompositionRefs` cannot see.
 * Every entry already carries a definite `version`, so it is always treated as pinned by the fetch
 * effect below and cached in the session-long published-version cache, never re-resolved against
 * "latest" at render time (spec: "resolves through that composition's own lockfile, not latest").
 */
function findLockfileRefs(resolution: SpaceDocument['resolution']): CompositionRef[] {
  if (!resolution) return [];
  const refs = new Map<string, CompositionRef>();
  resolution.entries.forEach((entry) => {
    const ref: CompositionRef = { systemName: entry.systemName, version: entry.version };
    refs.set(refCacheKey(ref), ref);
  });
  return Array.from(refs.values());
}

export interface UseCompositionResolverResult {
  /** `undefined` when the document has no composition refs — matches `SpaceRendererOrganism`'s "no registry attached" fast path. */
  resolveComposition: CompositionResolver | undefined;
  /**
   * True until the prefetch for the CURRENT document has settled — callers hold the first render
   * (skeleton) to avoid a placeholder flash for refs that will resolve. Derived from "which
   * document was this map resolved for", not flipped by an effect, so it is already true on the
   * first render: a caller that gates on it mounts its subtree once, after resolution, instead of
   * mounting → unmounting → remounting around a loader that only appears post-paint.
   */
  isResolving: boolean;
}

/**
 * Prefetches every composition a Space document references into a synchronous
 * `CompositionResolver`, since `expandComposition` cannot itself perform network I/O. A 404
 * resolves to `undefined` (falls through to the unknown-component placeholder — correct for a
 * genuinely unknown type); a non-404 failure is logged and also resolves to `undefined` rather
 * than throwing, so one bad ref never crashes the whole Space.
 */
export function useCompositionResolver(
  document: SpaceDocument | null,
  apiClient: ApiClient | undefined
): UseCompositionResolverResult {
  // Unpinned refs (no version pin) are cached per Space-instance mount only — reset whenever the
  // document identity changes, since an unpublished draft can move under an unpinned ref.
  const unpinnedCacheRef = useRef<Map<string, CompositionDefinition>>(new Map());
  // The raw context rather than `useImpersonation()`: that hook throws outside a provider, and this
  // hook must also run in hosts that mount no impersonation layer (tests, stories). No provider
  // means no in-session tenant switch is possible there, which is exactly one bucket.
  const actingOrgId = useContext(ImpersonationContext)?.impersonationDetails?.organizationId;
  // Keyed by the document it was resolved for: `resolvedFor !== document` IS "not settled yet",
  // which is what makes `isResolving` correct on the first render and on every document swap
  // without a second state flip.
  const [resolved, setResolved] = useState<{
    document: SpaceDocument | null;
    map: Map<string, CompositionDefinition>;
  }>({ document: null, map: new Map() });

  // Lockfile-backed whenever the artifact has one (written by the backend on every create/update/
  // rebase) — falls back to today's top-level-only, version-unaware discovery only for documents
  // written before EAINT-891's backfill.
  const lockfileVersionBySystemName = useMemo(() => {
    if (!document?.resolution) return null;
    // Fallback lookup for refs with NO pin of their own. `computeLockfile` resolves an unpinned
    // ref to the latest version, so when one `systemName` is locked at several versions (a mix of
    // pinned and unpinned placements) the highest is the one the unpinned placement resolved to —
    // deterministic, unlike "whichever entry the array happened to list first".
    const map = new Map<string, number>();
    document.resolution.entries.forEach((entry) => {
      const current = map.get(entry.systemName);
      if (current === undefined || entry.version > current) map.set(entry.systemName, entry.version);
    });
    return map;
  }, [document]);

  const refs = useMemo(() => {
    if (document?.resolution) return findLockfileRefs(document.resolution);
    return document ? findCompositionRefs(document) : [];
  }, [document]);
  const needsResolution = Boolean(apiClient) && refs.length > 0;
  const isResolving = needsResolution && resolved.document !== document;

  useEffect(() => {
    unpinnedCacheRef.current = new Map();
    // ...and on a tenant switch, for the same reason the published cache is org-scoped: the
    // document object does not change across an impersonation switch, so `[document]` alone would
    // let the previous tenant's definitions survive it.
  }, [actingOrgId, document]);

  useEffect(() => {
    if (!needsResolution || !apiClient) return undefined;

    let cancelled = false;

    (async () => {
      const results = new Map<string, CompositionDefinition>();

      await Promise.all(
        refs.map(async (ref) => {
          const key = refCacheKey(ref);
          const isPinned = ref.version !== undefined;
          const cache = isPinned ? publishedCacheForOrg(actingOrgId) : unpinnedCacheRef.current;
          const cached = cache.get(key);
          if (cached) {
            results.set(key, cached);
            return;
          }

          try {
            // The pin travels on the wire: `GET /compositions/:systemName?version=N` returns that
            // exact version (404 if the caller may not see it), so a document pinning version 2 can
            // never be rendered from version 5's definition.
            const path = `/compositions/${encodeURIComponent(ref.systemName)}${isPinned ? `?version=${ref.version}` : ''}`;
            const data = await apiClient.get<{ composition: CompositionResponse }>(path);
            const definition = data?.composition?.definition;
            if (!definition) return; // published-but-empty or draft-only for this viewer — unresolved
            // Only a *published* version is immutable, so only a published version may enter the
            // session-long cache. An editor's `latest` is their draft, which moves under them.
            // `cancelled` gates the WRITE, not just `setResolved`: this run may have been
            // superseded by a tenant switch while the request was in flight, and its response was
            // fetched with the previous tenant's identity.
            if (!cancelled && (!isPinned || data.composition.is_published === true)) cache.set(key, definition);
            results.set(key, definition);
          } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status !== 404) {
              // eslint-disable-next-line no-console
              console.error('[useCompositionResolver] failed to fetch composition', ref.systemName, err);
            }
          }
        })
      );

      if (!cancelled) {
        setResolved({ document, map: results });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [actingOrgId, apiClient, document, needsResolution, refs]);

  const resolveComposition: CompositionResolver | undefined = useMemo(() => {
    if (refs.length === 0) return undefined;
    if (lockfileVersionBySystemName) {
      // A ref nested inside a fetched composition's own subtree carries whatever
      // `componentVersion` its AUTHOR wrote (often unpinned/`undefined`) — for those, the lockfile
      // is what actually pins it, so substitute the locked version rather than resolving against
      // "latest" (spec: "resolves through that composition's own lockfile, not latest").
      //
      // A ref that DOES carry its own pin wins over the lockfile lookup. One `systemName` can
      // legitimately appear at two versions in the same document (`kpi_card` placed once at
      // `componentVersion: 2` and once unpinned yields two lockfile entries, and
      // `findLockfileRefs` fetches both); resolving such a ref by name alone would render the
      // pinned placement from whichever version happened to be listed first.
      return (ref: CompositionRef) => {
        const version = ref.version ?? lockfileVersionBySystemName.get(ref.systemName);
        if (version === undefined) return undefined;
        return resolved.map.get(refCacheKey({ systemName: ref.systemName, version }));
      };
    }
    return (ref: CompositionRef) => resolved.map.get(refCacheKey(ref));
  }, [refs, resolved, lockfileVersionBySystemName]);

  return { resolveComposition, isResolving };
}
